import { CSSProperties, useState, useCallback, useMemo, ChangeEvent } from "react";
import {
  Dimmer,
  Form,
  FormField,
  Input,
  List,
  ListContent,
  ListItem,
  Loader,
  TextArea,
} from "semantic-ui-react";

// Commons
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Components
import SegmentBroadcastList from "./SegmentBroadcastList";
import ModMeForm from "./ModMeForm";

// Serializers
import DiagRuleSerializer from "@/issara-sdk/types/DiagRuleSerializer_apps_PRX";
import PublishedDiagRuleNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";

// Types
import { TreeNode, FlatNode, ParsedNode, PickedState, RunSequence, SetProp, BroadcastNodeList } from "./MeFormType";

// Utils
import { updateContent } from "./MeFormUtils";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

// Interface
import { BTN_ACTS, STYLES } from "./sequence/MeForm";

type CardMeFormEditContentProps = {
  // func
  runSequence: RunSequence;
  setProp: SetProp;
  // data
  isActive?: boolean;
  isPublished?: boolean;
  detail?: DiagRuleSerializer;
  content: string;
  selectedNode: TreeNode | null;
  pdrList: PublishedDiagRuleNodeSerializer[];
  broadcastNodeList: BroadcastNodeList;

  onChangeContent: (newContent: string) => void;
  onOpenPostBroadcast: (node: FlatNode | TreeNode | null) => void;
} & Pick<PickedState, "buttonLoadCheck" | "BillingCreditWallet" | "loadingStatus">;

const RenderRequiredNodeList = ({
  missingTextNodes,
  missingGroupNodes
}: {
  missingTextNodes: FlatNode[];
  missingGroupNodes: FlatNode[]
}) => {
  const scrollStyle: CSSProperties = {
    maxHeight: "200px",
    overflowY: "auto",
    padding: "10px 0",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
      {missingTextNodes.length > 0 && (
        <div>
          <b>Required <span style={{ color: "red" }}>Evaluation Result / Suggestions</span> at Node:</b>
          <div style={scrollStyle}>
            <List divided relaxed>
              {missingTextNodes.map(n => (
                <ListItem key={n.id}>
                  <ListContent>{n.condition || `(Node ID: ${n.id}) unknown node name`}</ListContent>
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      )}

      {missingGroupNodes.length > 0 && (
        <div>
          <b>Required <span style={{ color: "red" }}>Group Name</span> at Node:</b>
          <div style={scrollStyle}>
            <List divided relaxed>
              {missingGroupNodes.map(n => (
                <ListItem key={n.id}>
                  <ListContent>{n.condition || `(Node ID: ${n.id}) unknown node name`}</ListContent>
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      )}
    </div>
  );
};

const CardMeFormEditContent = (props: CardMeFormEditContentProps) => {
  const [openRequiredCheckMod, setOpenRequiredCheckMod] = useState<boolean>(false);
  const [missingTextNodes, setMissingTextNodes] = useState<FlatNode[]>([]);
  const [missingGroupNodes, setMissingGroupNodes] = useState<FlatNode[]>([]);

  const formDetail = useMemo(() => props.detail, [props.detail]);

  const currentNode = useMemo(() => {
    if (!props.selectedNode) return null;

    try {
      const parsed: ParsedNode = JSON.parse(props.content);

      return parsed[props.selectedNode.id] || null;
    } catch (err) {
      return props.selectedNode;
    }
  }, [props.content, props.selectedNode]);

  const isQuestion = currentNode?.type === "choices";
  const isPublished = formDetail?.published === true;

  // --- Validate ทั้ง Form
  // const validateRequiredNodes = useCallback(() => {
  //   const parsed: ParsedNode = JSON.parse(props.content);
  //   const textMissing: FlatNode[] = [];
  //   const groupMissing: FlatNode[] = [];

  //   Object.values(parsed).forEach((node) => {
  //     if (node.type === "end") {
  //       if (!node.text || node.text.trim() === "") textMissing.push(node);
  //       if (!node.group_name || node.group_name.trim() === "") groupMissing.push(node);
  //     }
  //   });

  //   setMissingTextNodes(textMissing);
  //   setMissingGroupNodes(groupMissing);

  //   return textMissing.length === 0 && groupMissing.length === 0;
  // }, [props.content]);

  // --- Validate เฉพาะ Node ที่เลือก
  const validateSelectedNode = useCallback(() => {
    const parsed: ParsedNode = JSON.parse(props.content);
    const node = parsed[props.selectedNode?.id || 0];

    if (!node) return true;

    const missingText: FlatNode[] = [];
    const missingGroup: FlatNode[] = [];

    // --- Validate Question Node --- //
    if (node.type === "choices") {
      if (!node.text || node.text.trim() === "") {
        missingText.push(node);
      }
    }

    // --- Validate Answer Node --- //
    if (node.type === "end") {
      if (!node.text || node.text.trim() === "") {
        missingText.push(node);
      }

      if (!node.group_name || node.group_name.trim() === "") {
        missingGroup.push(node);
      }
    }

    setMissingTextNodes(missingText);
    setMissingGroupNodes(missingGroup);

    return missingText.length === 0 && missingGroup.length === 0;
  }, [props.content, props.selectedNode]);

  const handleChangeGroupName = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newGroupName = e.target.value;

      if (!currentNode) return;

      const newContent = updateContent(props.content, (parsed) => {
        const node = parsed[currentNode!.id];

        if (!node) return;
        node.group_name = newGroupName;
      });

      props.onChangeContent(newContent);
    },
    [currentNode, props.content, props.onChangeContent]
  );

  const handleChangeLabel = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newLabel = e.target.value;

      if (!currentNode) return;

      const newContent = updateContent(props.content, (parsed) => {
        const node = parsed[currentNode!.id];

        if (!node) return;

        node.label = newLabel;
      });

      props.onChangeContent(newContent);
    },
    [currentNode, props.content, props.onChangeContent]
  );

  const handleChangeText = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;

      if (!currentNode) return;

      const newContent = updateContent(props.content, (parsed) => {
        const node = parsed[currentNode!.id];

        if (!node) return;

        node.text = newText;
      });

      props.onChangeContent(newContent);
    },
    [currentNode, props.content, props.onChangeContent]
  );

  const handleSaveForm = () => {
    if (!formDetail?.id) return;

    props.runSequence({
      sequence: "MeForm",
      action: "SAVE_FORM",
      formId: formDetail?.id,
      data: {
        content: props.content,
      },
    });
  };

  const handleSaveFormWithCheck = () => {
    const isValid = validateSelectedNode();

    if (!isValid) {
      setOpenRequiredCheckMod(true);

      return;
    }

    handleSaveForm();
  }

  const handleCloseRequiredCheckMod = () => {
    setOpenRequiredCheckMod(false);
    setMissingTextNodes([]);
    setMissingGroupNodes([]);
  };

  // * ----- NOTE -----
  /*
  - QUESTION -> ให้ label เป็น Description for question
  - ANSWER   -> ให้ label เป็น Group Name
  */

  return (
    <div style={{ margin: "4px" }}>
      <Form>
        {isQuestion ? (
          <FormField>
            <label>Description for question</label>
            <TextArea
              disabled={props.isPublished}
              value={currentNode?.label}
              rows={7}
              fluid
              onChange={handleChangeLabel}
            />
          </FormField>
        ) : (
          <>
            <FormField inline>
              <label>
                Group name<span style={{ color: "red" }}>*</span>
              </label>
              <Input
                // disabled={props.isPublished}
                placeholder=""
                fluid
                value={currentNode?.group_name}
                onChange={handleChangeGroupName}
              />
            </FormField>
            <FormField>
              <label>
                Evaluation Result / Suggestions<span style={{ color: "red" }}>*</span>
              </label>
              <TextArea
                disabled={props.isPublished}
                value={currentNode?.text}
                onChange={handleChangeText}
              />
            </FormField>
          </>
        )}
        <FormField>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                fontStyle: "italic",
                color: "grey",
                fontSize: "smaller",
              }}
            >
              {`Last updated by ${currentNode?.edit_user_name ?? ""} ${
                formatISOtoBEDateTime(formDetail?.updated).dateTimeShortBracket || ""
              }`}
            </div>

            <ButtonLoadCheck
              // func
              setProp={props.setProp}
              paramKey={BTN_ACTS.SAVE_FORM}
              buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.SAVE_FORM}`]}
              onClick={handleSaveFormWithCheck}
              // config
              style={{
                ...STYLES.primaryThemeBtn,
              }}
              title="Save"
            />
          </div>
        </FormField>

        {!isQuestion && props.isPublished && props.isActive && (
          <FormField>
            <SegmentBroadcastList
              runSequence={props.runSequence}
              // data
              selectedNode={currentNode}
              pdrList={props.pdrList}
              broadcastNodeList={props.broadcastNodeList}
              onOpenPostBroadcast={props.onOpenPostBroadcast}
              // common
              BillingCreditWallet={props.BillingCreditWallet}
              loadingStatus={props.loadingStatus}
            />
          </FormField>
        )}
      </Form>

      <ModMeForm
        isOneButton
        open={openRequiredCheckMod}
        onClose={handleCloseRequiredCheckMod}
        onConfirm={handleCloseRequiredCheckMod}
        header="Please enter a required data of this node(s) before saving"
        content={
          <RenderRequiredNodeList
            missingTextNodes={missingTextNodes}
            missingGroupNodes={missingGroupNodes}
          />
        }
      />
    </div>
  );
};

export default CardMeFormEditContent;
